import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { inputClass } from "@/components/Panel";
import { Badge } from "@/components/ui";
import { auditDetailQuery, auditListQuery, auditTypesQuery, listAudit, type AuditRow } from "@/features/audit/api";
import { AuditPanel } from "@/features/audit/AuditPanel";
import { memberListQuery } from "@/features/users/api";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";

// Settings → Audit log: every change written by the audit triggers, newest
// first, filtered by what (table) or who, and by days. Read-only, export-only.

interface Search {
  type: string;
  actor: string;
  from: string;
  to: string;
  entry?: string | undefined;
}

const day = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "");

export const Route = createFileRoute("/_app/settings/audit-log")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      type: typeof raw.type === "string" ? raw.type.slice(0, 63) : "",
      actor: typeof raw.actor === "string" && /^[0-9a-f-]{36}$/i.test(raw.actor) ? raw.actor : "",
      from: day(raw.from),
      to: day(raw.to),
    };
    if (typeof raw.entry === "string" && raw.entry.includes("|")) s.entry = raw.entry;
    return s;
  },
  component: AuditLogPage,
});

const actionTone = { insert: "success", update: "info", delete: "danger" } as const;

function AuditLogPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canRead = useCan("core.audit.read");
  const canSeeUsers = useCan("core.user.read");
  const types = useQuery({ ...auditTypesQuery, enabled: canRead }).data ?? [];
  const memberPages = useInfiniteQuery({ ...memberListQuery(), enabled: canSeeUsers }).data?.pages;
  const members = useMemo(() => memberPages?.flatMap((p) => p.rows) ?? [], [memberPages]);
  const names = useMemo(() => new Map(members.map((m) => [m.user_id, m.full_name || m.email])), [members]);

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = { type: search.type, actor: search.type ? "" : search.actor, from: search.from, to: search.to };
  const query = useInfiniteQuery({ ...auditListQuery(params), enabled: canRead });
  const actorName = useCallback(
    (id: string | null, fallback?: string | null) => (id ? (names.get(id) ?? fallback ?? t("audit.unknownUser")) : t("audit.system")),
    [names, t],
  );

  const columns: Column<AuditRow>[] = [
    { key: "time", header: t("audit.colTime"), width: "minmax(130px,0.8fr)", className: "tabular text-ink-muted", cell: (r) => fmt.dateTime(r.occurred_at) },
    {
      key: "what",
      header: t("audit.colWhat"),
      width: "minmax(220px,2fr)",
      cell: (r, selected) => (
        <span className="truncate">
          <span className="font-mono text-xs text-ink-muted">{r.entity_type}</span>{" "}
          <span className={cn("font-medium", selected && "text-accent-ink")}>{r.label ?? r.entity_id}</span>
        </span>
      ),
    },
    { key: "action", header: t("audit.colAction"), width: "minmax(90px,0.6fr)", cell: (r) => <Badge tone={actionTone[r.action]}>{t(`audit.action.${r.action}`)}</Badge> },
    {
      key: "fields",
      header: t("audit.colFields"),
      width: "minmax(160px,1.4fr)",
      className: "truncate font-mono text-xs text-ink-muted",
      cell: (r) => (r.changed.length ? r.changed.join(", ") : "—"),
    },
    { key: "who", header: t("audit.colWho"), width: "minmax(130px,1fr)", className: "truncate", cell: (r) => actorName(r.actor_id, r.actor_name) },
  ];

  const exportColumns: ExportColumn<AuditRow>[] = [
    { header: t("audit.colTime"), kind: "text", value: (r) => r.occurred_at },
    { header: t("audit.colTable"), kind: "code", value: (r) => r.entity_type },
    { header: t("audit.colRecord"), kind: "text", value: (r) => r.label ?? r.entity_id },
    { header: "ID", kind: "text", value: (r) => r.entity_id },
    { header: t("audit.colAction"), kind: "code", value: (r) => r.action },
    { header: t("audit.colFields"), kind: "text", value: (r) => r.changed.join(", ") },
    { header: t("audit.colWho"), kind: "text", value: (r) => actorName(r.actor_id, r.actor_name) },
  ];

  if (!canRead) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("audit.title")}</h1>
        <p className="text-[13px] text-ink-muted">{t("audit.noPermission")}</p>
      </div>
    );
  }

  const listTools: ListTools<AuditRow> = {
    id: "audit-log",
    title: t("audit.title"),
    export: { fileBase: "audit-log", sheet: t("audit.title"), columns: exportColumns, fetchPage: (cursor, signal) => listAudit(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.settingsGroup.title")} <span aria-hidden="true">/</span> {t("nav.settingsGroup.auditLog")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("audit.title")}</h1>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-2 text-[13px]">
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("audit.filterTable")}
          <select value={search.type} onChange={(e) => setSearch({ type: e.target.value, actor: "" })} className={cn(inputClass, "w-48")}>
            <option value="">{t("audit.all")}</option>
            {types.map((ty) => (
              <option key={ty} value={ty}>
                {ty}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("audit.filterUser")}
          <select
            value={search.actor}
            disabled={!!search.type || !canSeeUsers}
            onChange={(e) => setSearch({ actor: e.target.value })}
            className={cn(inputClass, "w-56")}
          >
            <option value="">{t("audit.all")}</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name ? `${m.full_name} · ${m.email}` : m.email}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("audit.from")}
          <input type="date" value={search.from} onChange={(e) => setSearch({ from: e.target.value })} className={cn(inputClass, "w-40")} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("audit.to")}
          <input type="date" value={search.to} onChange={(e) => setSearch({ to: e.target.value })} className={cn(inputClass, "w-40")} />
        </label>
        {search.type && <p className="pb-2 text-xs text-ink-muted">{t("audit.oneFilter")}</p>}
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("audit.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => `${r.id}|${r.occurred_at}`}
          selectedId={search.entry}
          onSelect={(r) => setSearch({ entry: `${r.id}|${r.occurred_at}` })}
          onClear={() => setSearch({ entry: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(auditDetailQuery(`${r.id}|${r.occurred_at}`))}
          emptyTitle={t("audit.emptyTitle")}
          emptyBody={t("audit.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ type: "", actor: "", from: "", to: "", entry: undefined })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={760}
          tools={listTools}
        />
        <DetailPanel
          label={t("audit.detailLabel")}
          open={!!search.entry}
          placeholder={<DetailPlaceholder title={t("audit.selectPrompt")} hint={t("audit.selectPromptHint")} />}
        >
          {search.entry && (
            <AuditPanel
              key={search.entry}
              selectionKey={search.entry}
              actorName={(id) => actorName(id)}
              onClose={() => setSearch({ entry: undefined })}
            />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
